// apps/web/src/components/chat/forms/TrialProcessingForm.tsx
import {
  Button,
  Typography,
  Box,
  Paper,
  LinearProgress,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useState, useEffect, useCallback } from 'react';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { getResource, listResource } from '@diggens/api-client';

interface TrialProcessingData {
  submission_id: string;
  company_name: string;
  period: string;
  status: string;
}

interface TrialProcessingFormProps {
  data: TrialProcessingData;
  tenantId: string;
  onSuccess: (result: { action: string; data?: any }) => void;
  onClose: () => void;
}

export function TrialProcessingForm({ data, tenantId: _tenantId, onSuccess, onClose }: TrialProcessingFormProps) {
  const [status, setStatus] = useState<string>(data.status);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);

  // Fetch results and transition to TrialResultsForm
  const fetchResultsAndTransition = useCallback(async () => {
    try {
      // Fetch metrics — tenant_id is injected server-side, don't pass as filter
      const metricsResult = await listResource('metrics', {
        submission_id: data.submission_id,
        select: 'id, value, confidence, source_location, metric_definition_id',
      });
      const metrics = metricsResult.data;

      // Fetch metric definitions separately
      const metricDefIds = metrics?.map((m: any) => m.metric_definition_id).filter(Boolean) || [];

      let metricDefs: any[] = [];
      if (metricDefIds.length > 0) {
        const uniqueIds = [...new Set(metricDefIds)];
        const defsResult = await listResource('metric_definitions', {
          select: 'id, name, description, data_type',
          id: `in.(${uniqueIds.join(',')})`,
          limit: 1000,
        });
        metricDefs = defsResult.data;
      }

      // Create a map for quick lookup
      const defMap = new Map(metricDefs.map((d: any) => [d.id, d]));

      // Format metrics for TrialResultsForm
      const formattedMetrics = (metrics || []).map((m: any) => {
        const def = defMap.get(m.metric_definition_id);
        return {
          name: def?.name || 'Unknown Metric',
          value: m.value,
          confidence: m.confidence,
          source_location: m.source_location,
        };
      });

      // Transition to results form
      onSuccess({
        action: 'show_results',
        data: {
          submission_id: data.submission_id,
          company_name: data.company_name,
          period: data.period,
          metrics_extracted: formattedMetrics.length,
          metrics: formattedMetrics,
        },
      });
    } catch (err) {
      console.error('Error fetching results:', err);
      setError(`Failed to load results: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }, [data.submission_id, data.company_name, data.period, onSuccess]);

  // Poll for submission status
  const checkStatus = useCallback(async () => {
    try {
      const submission = await getResource<{ status: string; completeness: any }>('submissions', data.submission_id, {
        select: 'status, completeness',
      });

      if (!submission) return;

      setStatus(submission.status);

      // Update progress based on status
      if (submission.status === 'processing') {
        // Estimate progress (smooth animation)
        setProgress((prev) => Math.min(prev + 5, 95));
      } else if (submission.status === 'completed') {
        setProgress(100);
        // Fetch extracted metrics and transition to results
        await fetchResultsAndTransition();
      } else if (submission.status === 'failed') {
        setError('Extraction failed. Please try again.');
        setProgress(0);
      }
    } catch (err) {
      console.error('Error checking status:', err);
    }
  }, [data.submission_id, fetchResultsAndTransition]);

  // Poll every 3 seconds — start immediately regardless of initial status
  // so `submitted` -> `processing` transitions are picked up
  useEffect(() => {
    if (status === 'completed' || status === 'failed') return;

    // Fire an immediate check so we don't wait 3s for the first update
    checkStatus();

    const interval = setInterval(checkStatus, 3000);
    return () => clearInterval(interval);
  }, [status, checkStatus]);

  // TODO: need api-client equivalent for realtime subscriptions (postgres_changes)
  // Previously subscribed to realtime updates on submissions table for faster feedback

  return (
    <Box sx={{ p: 3, maxWidth: 600 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <CircularProgress size={24} />
        <Typography variant="h6">Extracting Metrics...</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Company Info */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Company
          </Typography>
          <Typography variant="body1">{data.company_name}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Period
          </Typography>
          <Typography variant="body1">{data.period}</Typography>
        </Box>
      </Paper>

      {/* Progress */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Analyzing document...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {Math.round(progress)}%
          </Typography>
        </Box>
        <LinearProgress variant="determinate" value={progress} />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          This typically takes 1-2 minutes. Please wait while we analyze your document.
        </Typography>
      </Box>

      {/* Status Messages */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <AutoAwesomeIcon fontSize="small" color="primary" />
          <Typography variant="body2" fontWeight="medium">
            What's happening:
          </Typography>
        </Box>
        <Box component="ul" sx={{ m: 0, pl: 2 }}>
          <Typography component="li" variant="body2" color="text.secondary">
            Reading and analyzing your document
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary">
            Extracting financial metrics and data points
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary">
            Verifying accuracy and confidence scores
          </Typography>
        </Box>
      </Paper>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button onClick={onClose} disabled={status === 'processing'}>
          Close
        </Button>
      </Box>
    </Box>
  );
}
